import Config from "../../../core/config/Config";
import CharacterLibrary from "../../characters/model/CharacterLibrary";
import DebugModel from "../../debug/model/DebugModel";
import TrackerScore from "../model/TrackerScore";
import ProgressModel from "../../progress/model/ProgressModel";
import StatusScore from "../model/StatusScore";

export default class ScoreService {
    /**
     * @constructor
     * @param {Config} config 
     * @param {ProgressModel} progressModel 
     * @param {CharacterLibrary} characterLibrary 
     */
    constructor(config, progressModel, characterLibrary) {
        this._config = config;
        this._progressModel = progressModel;
        this._characterLibrary = characterLibrary;

        /** @type {{ id: string, maxValue: number }[]} */
        this._trackerConfiguration = this._config.getValue(`trackers`);
        this._primaryTrackerScoreId = `groupSpirit`;

        /** @type {{id: string, requiredScore: number}[]} */
        this._unorderedStatuses = this._config.getValue(`scoring.statuses`);
        this._orderedStatusesDesc = this._unorderedStatuses.sort((a, b) => b.requiredScore - a.requiredScore); // Sort descending.
    }

    getTrackerScores() {
        const trackerScores = this._calculateScores(this._trackerConfiguration);
        this._applyScoreDebugOverrides(trackerScores);

        return trackerScores;
    }

    getPrimaryTrackerScore() {
        return this.getTrackerScores().find(trackerScore => trackerScore.id === this._primaryTrackerScoreId);
    }

    /**
     * Gets the status based on the score.
     * This is a tag/ rank the player can earn based on their score (primary tracker).
     * @param {StatusScore} score 
     */
    getStatusScore() {
        const score = this.getPrimaryTrackerScore().score;
        for (const status of this._orderedStatusesDesc) {
            if (score >= status.requiredScore) {
                return new StatusScore(status.id);
            }
        }

        // Default to last.
        const statusScoreId = this._orderedStatusesDesc[this._orderedStatusesDesc.length - 1].id;
        return new StatusScore(statusScoreId);
    }

    _calculateScores() {
        const characters = this._characterLibrary.characters;

        const trackerScores = [];
        for (const trackerConfiguration of this._trackerConfiguration) {
            const trackerId = trackerConfiguration.id;
            const trackerMaxValue = trackerConfiguration.maxValue;
            const trackerCumulativeScore = characters.reduce((acc, character) => {
                const tracker = character.getTrackerById(trackerId);
                return acc + tracker.value;
            }, 0);

            const trackerScore = new TrackerScore(trackerId, trackerCumulativeScore, trackerMaxValue);
            trackerScores.push(trackerScore);
        }

        return trackerScores;
    }

    /**
     * @param {TrackerScore[]} trackerScores 
     */
    _applyScoreDebugOverrides(trackerScores) {
        if (DebugModel.hasTrustOverride()) {
            trackerScores.find(trackerScore => trackerScore.id === `trust`).score = DebugModel.trust;
        }
        if (DebugModel.hasGroupSpiritOverride()) {
            trackerScores.find(trackerScore => trackerScore.id === `groupSpirit`).score = DebugModel.groupSpirit;
        }
    }
}
